/** Formatação compartilhada pelas telas do DP. */

/**
 * Data civil (`@db.Date`) vinda do backend em ISO.
 *
 * Formatar com `timeZone: "UTC"` é obrigatório: o backend guarda meia-noite UTC e,
 * sem isso, o navegador em America/Sao_Paulo (UTC-3) exibiria o dia ANTERIOR.
 */
export const formatDateOnly = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
};

/** Data civil ISO → 'YYYY-MM-DD', para preencher `<input type="date">`. */
export const toDateInputValue = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

export const formatCpf = (value?: string | null): string => {
  const digits = String(value ?? "").replace(/\D/g, "").slice(0, 11);
  if (!digits) return "—";
  return digits
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2");
};

/** Máscara progressiva de CPF, para usar no `onChange` de um input. */
export const maskCpfInput = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  return digits
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2");
};

export const formatCnpj = (value?: string | null): string => {
  const digits = String(value ?? "").replace(/\D/g, "").slice(0, 14);
  if (!digits) return "—";
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

export const maskCnpjInput = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

/**
 * `Decimal` do Prisma chega como STRING no JSON (BUG-R05). Converter antes de
 * formatar — `Number(undefined)` viraria NaN na tela.
 */
export const formatCurrency = (value?: string | number | null): string => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

export const formatHours = (value?: string | number | null): string => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return `${num.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}h`;
};

export const orDash = (value?: string | null): string => {
  const text = String(value ?? "").trim();
  return text || "—";
};

/**
 * Remove chaves vazias antes do PATCH/POST.
 *
 * O backend trata `""` como "limpar o campo", mas mandar string vazia em todo
 * campo não preenchido de um formulário de ~40 campos apagaria dados sem que o
 * operador tenha pedido. Enviamos só o que foi digitado.
 */
export const stripEmpty = <T extends Record<string, unknown>>(payload: T): Partial<T> => {
  const result: Record<string, unknown> = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "string" && value.trim() === "") return;
    result[key] = typeof value === "string" ? value.trim() : value;
  });
  return result as Partial<T>;
};

export const MONTH_LABELS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

/** Competência "03/2026". */
export const formatCompetence = (year: number, month: number): string =>
  `${String(month).padStart(2, "0")}/${year}`;

/** "7,5%" a partir de um número ou Decimal em string. */
export const formatPercentValue = (value?: string | number | null): string => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return `${num.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}%`;
};

/** Baixa um blob com o nome dado (o CSV vem por rota autenticada). */
export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
